// Drift Team Website 2025
// src/pages/Home.jsx
// Created by Lukas Helebrandt on 17.03.2025.
// Updated by Grok on 23.04.2025 to handle settings errors gracefully
// Updated to include blog section and reorder content for optimal UX
// Updated by Claude on 20.05.2025 to add SEO optimization

import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSettings } from '../contexts/SettingsContext';
import Navbar from '../components/common/NavBar';
import Hero from '../components/home/Hero';
import About from '../components/home/About';
import TeamSection from '../components/home/TeamSection';
import CarsSection from '../components/home/CarsSection';
import DriftTaxiSection from '../components/home/DriftTaxiSection';
import EventsSection from '../components/home/EventsSection';
import AchievementsSection from '../components/home/AchievementsSection';
import GallerySection from '../components/home/GallerySection';
import ShopSection from '../components/home/ShopSection';
import SponsorsSection from '../components/home/SponsorsSection';
import LatestBlogPosts from '../components/home/LatestBlogPosts'; // Blog sekce
import ContactSection from '../components/home/ContactSection';
import Footer from '../components/common/Footer';
import SEO from '../components/common/SEO';
import { getOrganizationSchema } from '../utils/schema';
import { getUserLanguage } from '../utils/language';
import { IMAGE_BASE_URL } from '../config';

const Home = () => {
  const language = getUserLanguage();
  const location = useLocation();
  const { settings, loading, error } = useSettings();

  // Efekt pro scroll na hash sekci po načtení stránky
  useEffect(() => {
    const hash = location.hash;
    if (hash && !loading) {
      const sectionId = hash.replace('#', '');
      
      // Funkce pro pokus o scroll s opakováním
      const tryScroll = (attempts = 0) => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else if (attempts < 5) {
          // Zkusí to znovu po 200ms, maximálně 5x
          setTimeout(() => tryScroll(attempts + 1), 200);
        }
      };

      // Spustí první pokus po 300ms
      const timer = setTimeout(() => tryScroll(), 300);
      return () => clearTimeout(timer);
    }
  }, [location.hash, loading]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (error) {
    console.error('[Home] Settings error:', error); // Logování chyby
    return (
      <div className="min-h-screen bg-black">
        <Navbar language={language} />
        <div className="flex items-center justify-center min-h-[50vh] bg-black">
          <div className="text-white text-center">
            <h2 className="text-2xl font-bold mb-4">
              {language === 'cs' ? 'Něco se pokazilo' : 'Something went wrong'}
            </h2>
            <p className="text-gray-400">
              {language === 'cs'
                ? `Nepodařilo se načíst nastavení stránky: ${error}`
                : `Failed to load page settings: ${error}`}
            </p>
            <div className="mt-6 space-x-4">
              <button
                onClick={() => window.location.reload()}
                className="btn-drift"
              >
                {language === 'cs' ? 'Zkusit znovu' : 'Try again'}
              </button>
              {error.includes('Unauthorized') && (
                <button
                  onClick={() => window.location.href = '/login'}
                  className="btn-drift"
                >
                  {language === 'cs' ? 'Přihlásit se' : 'Log in'}
                </button>
              )}
            </div>
          </div>
        </div>
        <Footer
          instagram={settings.instagram_url}
          facebook={settings.facebook_url}
          youtube={settings.youtube_url}
          tiktok={settings.tiktok_url}
        />
      </div>
    );
  }

  // Prepare schema for homepage
  const organizationSchema = getOrganizationSchema(language);
  
  return (
    <div className="min-h-screen bg-black">
      <SEO
        title={language === 'cs' ? 'Domů' : 'Home'}
        description={settings.hero_description || 'NO RISK NO DRIFT je profesionální drift team z České Lípy. Závodíme, předvádíme a nabízíme zážitky s driftem.'}
        image={settings.og_image || `${IMAGE_BASE_URL}/images/logo.jpeg`}
        keywords="drift team, drifting, motorsport, česká lípa, drift show, drift taxi, závodní drift, drift zážitek"
        schema={organizationSchema}
        canonical={language === 'cs' ? 'https://norisknodrift.cz' : `${IMAGE_BASE_URL}`}
      />
      
      <Navbar language={language} />
      
      <Hero
        title={settings.hero_title}
        description={settings.hero_description}
        videoPath={settings.hero_video_path}
      />

      <About />

      <TeamSection />

      <CarsSection />

      <DriftTaxiSection />

      <EventsSection />

      <AchievementsSection />

      <GallerySection />

      <ShopSection />

      <SponsorsSection />

      <LatestBlogPosts />

      <ContactSection
        email={settings.contact_email}
        phone={settings.contact_phone}
        address={settings.contact_address}
      />

      <Footer
        instagram={settings.instagram_url}
        facebook={settings.facebook_url}
        youtube={settings.youtube_url}
        tiktok={settings.tiktok_url}
      />
    </div>
  );
};

export default Home;
